/**
 * This is the entry point for the frontend application. It is referenced in the `index.html` file.
 * It sets up the React application, including the router and query client, and renders the app into the DOM.
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import ReactDOM from 'react-dom/client';
import './styles/reset.css';
import './styles/print-page.css';
import classNames from 'classnames';
import * as styles from './app.css';
import { routeTree } from './routeTree.gen.js';
import './styles/print.css';
import { themeClass } from './styles/theme.css';

// Set up a Router instance
const router = createRouter({
    routeTree,
    defaultPreload: 'intent',
    scrollRestoration: true,
});

// Register things for typesafety
declare module '@tanstack/react-router' {
    interface Register {
        router: typeof router;
    }
}

const bodyElement = document.body;
bodyElement.className = classNames(themeClass, styles.body);

const rootElement = document.getElementById('root')!;

if (!rootElement.innerHTML) {
    const root = ReactDOM.createRoot(rootElement);

    // Only exists for /sandbox (a dev-only route with no auth guard, bound to be
    // deleted eventually) — every real route gets its own client scoped to its
    // authenticated subtree, see the `_auth` layouts under `routes/system` and
    // `routes/_client`. No session-expiry wiring belongs here.
    const queryClient = new QueryClient();

    root.render(
        <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
        </QueryClientProvider>
    );
}
